import { z } from 'zod'

// Candidate novidades the model proposes from a week of commits.
//
// This is AI output that reaches a table, so it validates before any write — the universal
// testing floor, and the reason the generator can be run unattended without a human reading
// every field. The strictness here is doing real work: `.strict()` means a hallucinated extra
// key is a hard failure rather than silently-dropped data, and a non-empty `commitShas` is what
// makes the SHA dedup trustworthy (a cluster that cites nothing would be re-proposed forever
// and would also be unauditable).

/** Which screens the commits behind a suggestion touched. */
export const NovidadeSurface = z.enum(['portal', 'staff', 'pipeline', 'infra'])
export type NovidadeSurface = z.infer<typeof NovidadeSurface>

export const NovidadeSuggestion = z
  .object({
    /**
     * Short SHAs of every commit in this cluster. A feature is usually several commits — the
     * whole point of the AI step is grouping them — so this is an array, and it must name at
     * least one or the suggestion has no provenance and no dedup key.
     */
    commitShas: z.array(z.string().min(7).max(40)).min(1),
    /**
     * Where a user would notice the change. The generator computes this deterministically from
     * file paths and sends it IN, so the model echoing something different is a signal its
     * clustering merged unrelated commits — the script trusts its own value, not this one.
     */
    surface: NovidadeSurface,
    /** Neutral "what shipped", English or pt-PT. Reviewed in dry-run output, never published. */
    factualSummary: z.string().min(1).max(400),
    /** Proposed pt-PT title, client register. The admin rewrites it; this is a starting point. */
    titlePt: z.string().min(1).max(120),
    /** Proposed pt-PT body, client register, one or two sentences. */
    bodyPt: z.string().min(1).max(1200),
    /**
     * True when the change is specific to ONE client company rather than the platform. Nuno's
     * spam objection in one field: "podemos ter um bug fix específico numa situação para o
     * cliente que eu não digo nada e depois vai gerar muito spam". The generator refuses to
     * store these, and separately refuses anything whose text matches a real client name,
     * because a model's self-report is not a guard.
     */
    perClient: z.boolean(),
  })
  .strict()

export type NovidadeSuggestion = z.infer<typeof NovidadeSuggestion>

/** The model returns an object, not a bare array — structured-output mode requires a root object. */
export const NovidadeSuggestionBatch = z
  .object({
    suggestions: z.array(NovidadeSuggestion),
  })
  .strict()

export type NovidadeSuggestionBatch = z.infer<typeof NovidadeSuggestionBatch>
